import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// pdfjs-dist needs its worker script location configured before use in a
// browser context - CDN-hosted, matching the installed package version, so
// no bundler/webpack config changes are needed.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const SUPPORTED_EXTENSIONS = ['txt', 'docx', 'pdf'];

// Below this many non-whitespace characters, a PDF's "text layer" is treated
// as effectively absent (scanned/image-only) rather than a genuine, if short,
// native document - conservative enough that a short-but-real PDF isn't
// misclassified as scanned (see specs/2026-09-06-trainer-led-ai-evaluation-checklist.md).
const MIN_PDF_TEXT_LENGTH = 20;

export interface FileRef {
    filename: string;
    bloburl: string;
}

export interface ExtractionResult {
    ok: boolean;
    text: string;
    reason?: 'unsupported-format' | 'scanned-or-empty' | 'extraction-error';
}

function getExtension(filename: string): string {
    return (filename.split('.').pop() || '').toLowerCase();
}

export function isSupportedExtension(filename: string): boolean {
    return SUPPORTED_EXTENSIONS.includes(getExtension(filename));
}

async function extractTxt(arrayBuffer: ArrayBuffer): Promise<string> {
    return new TextDecoder('utf-8').decode(arrayBuffer);
}

async function extractDocx(arrayBuffer: ArrayBuffer): Promise<string> {
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
}

async function extractPdf(arrayBuffer: ArrayBuffer): Promise<string> {
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pageTexts: string[] = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const content = await page.getTextContent();
        // pdf.js returns individual text runs with no inherent line breaks -
        // reconstruct them from each run's vertical position (and its own
        // hasEOL flag, when present), otherwise every line on the page gets
        // mashed into one run-on line of text.
        const lines: string[][] = [[]];
        let lastY: number | null = null;
        for (const item of content.items as any[]) {
            const y = Array.isArray(item.transform) ? item.transform[5] : null;
            if (lastY !== null && y !== null && Math.abs(y - lastY) > 1) {
                lines.push([]);
            }
            lines[lines.length - 1].push(item.str);
            if (item.hasEOL) {
                lines.push([]);
            }
            if (y !== null) lastY = y;
        }
        pageTexts.push(lines.map(l => l.join(' ')).join('\n').trim());
    }
    return pageTexts.join('\n\n');
}

export async function extractTextFromFile(file: FileRef): Promise<ExtractionResult> {
    const extension = getExtension(file.filename);
    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
        return { ok: false, text: '', reason: 'unsupported-format' };
    }

    try {
        const response = await fetch(file.bloburl);
        const arrayBuffer = await response.arrayBuffer();

        let text: string;
        if (extension === 'txt') {
            text = await extractTxt(arrayBuffer);
        } else if (extension === 'docx') {
            text = await extractDocx(arrayBuffer);
        } else {
            text = await extractPdf(arrayBuffer);
        }

        if (extension === 'pdf' && text.trim().length < MIN_PDF_TEXT_LENGTH) {
            return { ok: false, text: '', reason: 'scanned-or-empty' };
        }

        return { ok: true, text };
    } catch {
        return { ok: false, text: '', reason: 'extraction-error' };
    }
}
